import { nanoid } from 'nanoid'

import { Section } from './sections/Section'
import { SubSection } from './sections/SubSection'
import { ImageInput } from './elements/ImageInput'
import { Globe, Phone, Mail } from 'lucide-react'
import { useMemo } from 'react'
import { getUseConfigStore } from '../stores/useConfigStore'
import { AsyncButton, Input, Separator, Textarea } from '@bpinternal/ui-kit-next'
import { Paywall } from '~/components/Paywall'
import useNavigationBlocker, { navigationBlockerOptions } from '../hooks/useNavigationBlocker'

type Props = {
  workspaceId: string
  botId: string
}

export function IdentityConfig({ workspaceId, botId }: Props) {
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])

  const botAvatar = useConfigStore((state) => state.botAvatar)
  const setBotAvatar = useConfigStore((state) => state.setBotAvatar)

  const botName = useConfigStore((state) => state.botName)
  const setBotName = useConfigStore((state) => state.setBotName)

  const botDescription = useConfigStore((state) => state.botDescription)
  const setBotDescription = useConfigStore((state) => state.setBotDescription)

  const composerPlaceholder = useConfigStore((state) => state.composerPlaceholder)
  const setComposerPlaceholder = useConfigStore((state) => state.setComposerPlaceholder)

  const footer = useConfigStore((state) => state.footer)
  const setFooter = useConfigStore((state) => state.setFooter)

  const email = useConfigStore((state) => state.email)
  const setEmail = useConfigStore((state) => state.setEmail)

  const phone = useConfigStore((state) => state.phone)
  const setPhone = useConfigStore((state) => state.setPhone)

  const privacyPolicy = useConfigStore((state) => state.privacyPolicy)
  const setPrivacyPolicy = useConfigStore((state) => state.setPrivacyPolicy)

  const termsOfService = useConfigStore((state) => state.termsOfService)
  const setTermsOfService = useConfigStore((state) => state.setTermsOfService)

  const website = useConfigStore((state) => state.website)
  const setWebsite = useConfigStore((state) => state.setWebsite)

  const saveConfig = useConfigStore((state) => state.saveConfig)
  const _updating = useConfigStore((state) => state._updating)
  const _originalConfig = useConfigStore((state) => state._originalConfig)

  const hasPendingChanges =
    botAvatar !== _originalConfig?.botAvatar ||
    botName !== _originalConfig?.botName ||
    botDescription !== _originalConfig?.botDescription ||
    composerPlaceholder !== _originalConfig?.composerPlaceholder ||
    footer !== _originalConfig?.footer ||
    email?.link !== _originalConfig?.email?.link ||
    phone?.link !== _originalConfig?.phone?.link ||
    privacyPolicy?.link !== _originalConfig?.privacyPolicy?.link ||
    termsOfService?.link !== _originalConfig?.termsOfService?.link ||
    website?.link !== _originalConfig?.website?.link

  useNavigationBlocker(hasPendingChanges, {
    ...navigationBlockerOptions,
    confirmCallback: async () => {
      try {
        await saveConfig()
        return true
      } catch (error) {
        console.log('Failed to save changes', error)
        return false
      }
    },
    cancelCallback: async () => {
      if (_originalConfig) {
        setBotAvatar(_originalConfig.botAvatar)
        setBotName(_originalConfig.botName)
        setBotDescription(_originalConfig.botDescription)
        setComposerPlaceholder(_originalConfig.composerPlaceholder)
        setFooter(_originalConfig.footer)
        setEmail(
          _originalConfig.email && _originalConfig.email.link && _originalConfig.email.title
            ? { link: _originalConfig.email.link, title: _originalConfig.email.title }
            : _originalConfig.email && (_originalConfig.email.link || _originalConfig.email.title)
              ? { link: _originalConfig.email.link ?? '', title: _originalConfig.email.title ?? '' }
              : undefined
        )
        setPhone(
          _originalConfig.phone && _originalConfig.phone.link && _originalConfig.phone.title
            ? { link: _originalConfig.phone.link, title: _originalConfig.phone.title }
            : _originalConfig.phone && (_originalConfig.phone.link || _originalConfig.phone.title)
              ? { link: _originalConfig.phone.link ?? '', title: _originalConfig.phone.title ?? '' }
              : undefined
        )
        setPrivacyPolicy(
          _originalConfig.privacyPolicy && _originalConfig.privacyPolicy.link && _originalConfig.privacyPolicy.title
            ? { link: _originalConfig.privacyPolicy.link, title: _originalConfig.privacyPolicy.title }
            : _originalConfig.privacyPolicy &&
                (_originalConfig.privacyPolicy.link || _originalConfig.privacyPolicy.title)
              ? { link: _originalConfig.privacyPolicy.link ?? '', title: _originalConfig.privacyPolicy.title ?? '' }
              : undefined
        )
        setTermsOfService(
          _originalConfig.termsOfService && _originalConfig.termsOfService.link && _originalConfig.termsOfService.title
            ? { link: _originalConfig.termsOfService.link, title: _originalConfig.termsOfService.title }
            : _originalConfig.termsOfService &&
                (_originalConfig.termsOfService.link || _originalConfig.termsOfService.title)
              ? { link: _originalConfig.termsOfService.link ?? '', title: _originalConfig.termsOfService.title ?? '' }
              : undefined
        )
        setWebsite(
          _originalConfig.website && _originalConfig.website.link && _originalConfig.website.title
            ? { link: _originalConfig.website.link, title: _originalConfig.website.title }
            : _originalConfig.website && (_originalConfig.website.link || _originalConfig.website.title)
              ? { link: _originalConfig.website.link ?? '', title: _originalConfig.website.title ?? '' }
              : undefined
        )
      }
      return false
    },
  })

  return (
    <>
      <Section
        name="Bot Identity"
        sticky
        actionButton={
          <AsyncButton
            className="hidden h-9 lg:block"
            onClick={saveConfig}
            disabled={_updating || !hasPendingChanges}
            loading={_updating}
          >
            Publish Changes
          </AsyncButton>
        }
      >
        <SubSection
          title="Bot Avatar"
          description="Upload an image to represent your bot in conversations."
          className="flex-row"
        >
          <div className="flex w-full justify-end">
            <ImageInput
              workspaceId={workspaceId}
              botId={botId}
              title="Bot Avatar"
              imgKey={`avatar-${nanoid(6)}`}
              imgUrl={botAvatar ?? ''}
              setImgUrl={setBotAvatar}
            />
          </div>
        </SubSection>
        <Separator className="my-10" />
        <SubSection title="Display Name" description="The name that appears in the chat header and conversations.">
          <Input
            className="h-9 w-full "
            value={botName}
            placeholder="My bot agent"
            onChange={(e) => setBotName(e.target.value)}
          />
        </SubSection>
        <Separator className="my-10" />
        <SubSection title="Bot Description" description="A brief description of your bot's purpose and capabilities.">
          <Textarea
            className="h-32 w-full resize-none"
            placeholder="Describe what your bot does"
            value={botDescription}
            onChange={(e) => setBotDescription(e.target.value)}
          />
        </SubSection>
        <Separator className="my-10" />
        <SubSection title="Message Placeholder" description="Placeholder text shown in the message input field.">
          <Input
            className="h-9 w-full "
            value={composerPlaceholder}
            placeholder="Type your message..."
            onChange={(e) => setComposerPlaceholder(e.target.value)}
          />
        </SubSection>
        <Separator className="my-10" />
        <Paywall minimumPlan="plus" analyticsEventName="footer" className="w-full">
          <SubSection title="Footer" description="Text displayed at the bottom of the chat interface.">
            <Input
              className="h-9 w-full "
              value={footer}
              placeholder="⚡️ by botpress"
              onChange={(e) => setFooter(e.target.value)}
            />
          </SubSection>
        </Paywall>
        <Separator className="my-10" />
      </Section>
      <Section name="Contact" forceNameDisplay>
        <SubSection title="Contact Information" description="Email, Phone and Web contact information for your bot.">
          <div className="flex w-full flex-col gap-3">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-10"
                placeholder="example@example.com"
                value={email?.link ?? ''}
                onChange={(e) => setEmail({ title: e.target.value, link: e.target.value })}
              />
            </div>

            <div className="relative">
              <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-10"
                placeholder="+1 555 555 5555"
                value={phone?.link ?? ''}
                onChange={(e) => setPhone({ title: e.target.value, link: e.target.value })}
              />
            </div>

            <div className="relative">
              <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-10"
                placeholder="https://example.com"
                value={website?.link ?? ''}
                onChange={(e) => setWebsite({ title: e.target.value, link: e.target.value })}
              />
            </div>
          </div>
        </SubSection>
        <Separator className="my-10" />
        <SubSection title="Terms of service" description="Link to your terms of service document.">
          <Input
            placeholder="https://example.com/terms"
            value={termsOfService?.link ?? ''}
            onChange={(e) => setTermsOfService({ title: 'Terms of service', link: e.target.value })}
          />
        </SubSection>
        <SubSection title="Privacy Policy" description="Link to your privacy policy document.">
          <Input
            placeholder="https://example.com/privacy"
            value={privacyPolicy?.link ?? ''}
            onChange={(e) => setPrivacyPolicy({ title: 'Privacy policy', link: e.target.value })}
          />
        </SubSection>
      </Section>
    </>
  )
}
